// Status presentation model for the Schedule Dashboard, shared by the
// scheduling-periods table so labels, pill variants and row actions stay in sync.
//
// The status union stays lower-case here; the backend's UPPER_CASE
// `LoadBalancingScheduleDashboardStatus` enum is mapped onto it at the query boundary.

import type { StatusPillVariantType } from '@common/components';

// Types

// The statuses the dashboard surfaces. The backend model also classifies `missed` (a
// lapsed period — load-balancing window closed with no run), `rejected` and `undefined`,
// which are never sent over GraphQL (missed/lapsed rows are dropped, not shown).
export type SchedulingPeriodStatusType =
  | 'balancing'
  | 'overdue'
  | 'self_scheduling'
  | 'workload_planning'
  | 'run_pending'
  | 'completed';

// Constants

// Compact, lifecycle-phase labels: the dashboard surfaces just four states. Every surfaced
// load-balancing-phase status collapses to the single "Balancing" label — run_pending (no
// reviewable run yet), balancing (awaiting the manager decision) and overdue (decision pending
// past the deadline) — so managers see one "in the balancing phase" state rather than its
// internal sub-states. Row actions and period links still key off the granular status (see
// actionsForStatus / periodLinkTo).
const STATUS_LABELS: Record<SchedulingPeriodStatusType, string> = {
  workload_planning: 'Workload Planning',
  self_scheduling: 'Self-Scheduling',
  run_pending: 'Balancing',
  balancing: 'Balancing',
  overdue: 'Balancing',
  completed: 'Completed',
};

// `StatusPill` variant per compact bucket: Workload Planning = neutral, Self-Scheduling =
// in progress, Balancing (every surfaced load-balancing-phase status) = warning, Completed =
// success.
const STATUS_PILL_VARIANTS: Record<
  SchedulingPeriodStatusType,
  StatusPillVariantType
> = {
  workload_planning: 'neutral',
  self_scheduling: 'inProgress',
  run_pending: 'warning',
  balancing: 'warning',
  overdue: 'warning',
  completed: 'success',
};

export type PeriodActionType = { label: string };

export type StatusActionsType = {
  primary: PeriodActionType;
  overflow: PeriodActionType[];
};

// Functions

/** Primary button + overflow menu items for a row, driven by its status. */
export function actionsForStatus(
  status: SchedulingPeriodStatusType
): StatusActionsType {
  switch (status) {
    case 'workload_planning':
    case 'self_scheduling':
    case 'run_pending':
      return { primary: { label: 'View Workload' }, overflow: [] };
    case 'balancing':
    case 'overdue':
      return {
        primary: { label: 'Review Schedule' },
        overflow: [{ label: 'Reset Schedule' }, { label: 'View Workload' }],
      };
    case 'completed':
      return {
        primary: { label: 'View Performance' },
        overflow: [
          { label: 'View Schedule' },
          { label: 'View Workload' },
          { label: 'Re-export' },
        ],
      };
  }
}

/**
 * The Period cell links — and the Review Schedule button appears — for periods
 * with a reviewable run (balancing / overdue). Completed periods stay plain text
 * until the published view is built.
 */
function isPeriodLinked(status: SchedulingPeriodStatusType): boolean {
  return status === 'balancing' || status === 'overdue';
}

type PeriodLinkParamsType = {
  serviceLineId: string;
  runId?: string;
  organizationUnitId?: string;
};

/**
 * Where the Period cell link (and Review Schedule button) navigates: the
 * unit-scoped Schedule Review session for the row's (run, organization unit) pair.
 *
 * Returns `undefined` when there is no destination (a pre-run or completed status,
 * or missing run/unit ids); callers render plain text / no button in that case.
 */
export function periodLinkTo(
  status: SchedulingPeriodStatusType,
  { serviceLineId, runId, organizationUnitId }: PeriodLinkParamsType
): string | undefined {
  if (isPeriodLinked(status) && runId && organizationUnitId) {
    return `/works/schedule/dashboard/${serviceLineId}/review/${runId}/${organizationUnitId}`;
  }

  return undefined;
}

export function statusLabel(status: SchedulingPeriodStatusType): string {
  return STATUS_LABELS[status];
}

export function statusPillVariant(
  status: SchedulingPeriodStatusType
): StatusPillVariantType {
  return STATUS_PILL_VARIANTS[status];
}

/**
 * Fill Rate cell value: a percentage to two decimal places (e.g. `84.62%`), or an
 * em dash when the run has no auto-balanced fill rate yet (`null`). The dashboard's
 * tabular view shows two decimals; the compact Health Strip rounds to whole numbers.
 */
export function formatFillRate(fillRate: number | null): string {
  return fillRate === null ? '—' : `${fillRate.toFixed(2)}%`;
}
